import { useState, useEffect, useCallback } from "react";
import { api } from "../api";
import { useDisplayTimezone } from "../portalConfig";
import { G_DARK, Badge, Btn, Card, formatWhen } from "../components/Shared";

// Why this screen exists at all: the portal's periodic work leaves no trace
// when it succeeds, so a timer that quietly stopped firing looks exactly like a
// timer with nothing to do. The **Overdue** mark below is what distinguishes
// them — a failure writes a row and a log entry, an absent scheduler writes
// nothing.
//
// It replaced a Missed column counting every occurrence in a fortnight, which
// stopped meaning anything the day the summary job became hourly: 336 of them,
// and "12 in the last 14 days" answers no question anybody has. Whether a
// message was *delivered* is a different question and has its own card below.

// **Every name in `SCHEDULES` needs an entry here.** There were three for four
// jobs, and `order_sweep` rendered as its raw name on an admin screen among
// three English labels — the map falls back to `j.job`, so nothing failed.
// `backend/tests/test_health_kind_contract.py` now fails instead.
const JOB_LABELS = {
  newsletter:      "Project Summaries",
  quote_expiry:    "Quotation Expiry",
  order_sweep:     "Order Sweep",
  retention_sweep: "Retention Sweep",
  log_sweep:       "Log Sweep",
  ppms_catalog:    "PPMS Catalogue",
};

// One line each on what the job actually does. None of the four said anywhere,
// and the retention one matters most: that it deletes the DATA as well as the
// record was on no screen in the portal.
const JOB_DESCRIPTIONS = {
  newsletter:      "Builds each subscriber's summary of running projects and queues it for "
                   + "delivery — only for those whose next one is due.",
  quote_expiry:    "Marks sent quotations whose deadline has passed as expired, so nothing can "
                   + "be signed after the quotation has lapsed.",
  order_sweep:     "Archives projects whose agreed order ran past the time the facility allows, "
                   + "one at a time with a reason recorded on each.",
  retention_sweep: "Deletes archived projects and their stored data once they outlive the "
                   + "facility's retention policy.",
  // Runs AFTER retention_sweep, and that is not arbitrary: deleting a project
  // is what releases its log entries, which the database refuses to delete
  // while the project exists.
  log_sweep:       "Removes log entries past the facility's retention. A project's own entries "
                   + "are kept for as long as the project exists, whatever the policy says.",
  // Wakes every hour and refreshes only in the hour set on Portal settings —
  // but Run now, since 2026-10-30, refreshes at once (the maintainer's call:
  // it had written "skipped — not the hour" on dev).
  ppms_catalog:    "Copies PPMS's whole project list into the portal, so any project can be "
                   + "found by name and imported. Runs once a day, in the hour set on Portal "
                   + "settings; Run now refreshes it at once, whatever the hour.",
};

const STATUS_LOOK = {
  ok:      { label: "OK",       color: "#059669" },
  error:   { label: "Failed",   color: "#dc2626" },
  running: { label: "Running",  color: "#2563eb" },
};

// The outbox's four states, and the third one is the reason this table is here.
// `sending` means the delivery attempt ended without an answer — a timeout, a
// connection dropped mid-handover, a pod killed at exactly the wrong moment.
// It is deliberately never retried, because retrying might deliver a second
// copy, so it has to stay visible rather than being tidied away.
const OUTBOX_LOOK = {
  pending: { label: "Queued",  color: "#6b7280" },
  sending: { label: "Unknown", color: "#d97706" },
  sent:    { label: "Sent",    color: "#059669" },
  failed:  { label: "Failed",  color: "#dc2626" },
};

const th = { padding: "10px 14px", textAlign: "left", fontSize: 12, fontWeight: 600,
             color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.4 };
const td = { padding: "10px 14px", fontSize: 14, color: "#374151", verticalAlign: "top" };

export default function AutomationTab() {
  const [status, setStatus]   = useState(null);
  const [outbox, setOutbox]   = useState(null);
  const [error, setError]     = useState(null);
  const [busy, setBusy]       = useState(null);
  const [notice, setNotice]   = useState(null);
  const zone = useDisplayTimezone();

  const load = useCallback(() => {
    Promise.all([api.get("/api/admin/automation"), api.get("/api/admin/outbox?limit=50")])
      .then(([s, o]) => { setStatus(s); setOutbox(o); setError(null); })
      .catch(e => setError(e.message));
  }, []);

  useEffect(load, [load]);

  const runNow = async job => {
    setBusy(job);
    setNotice(null);
    try {
      const res = await api.post(`/api/admin/automation/${job}/run`, {});
      setNotice(res.detail);
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  if (error)  return <Card><div role="alert" style={{ color: "#dc2626" }}>{error}</div></Card>;
  if (!status) return <Card>Loading…</Card>;

  return (
    <>
      <Card>
        <h3 style={{ margin: "0 0 4px", fontSize: 16 }}>Scheduled work</h3>
        <p style={{ margin: "0 0 16px", fontSize: 13, color: "#6b7280" }}>
          Times are {status.timezone}. A job that failed leaves a note here and an entry in the
          log; a job that never ran leaves nothing at all, so an <strong>Overdue</strong> mark on
          a row is what tells you the scheduler itself has stopped. Whether a message got
          <em> delivered</em> is a different question — that is Notifications queued, below.
        </p>

        {!status.scheduler_enabled && (
          <div style={{ padding: 12, marginBottom: 16, borderRadius: 8, fontSize: 13,
                        background: "#fef3c7", color: "#92400e" }}>
            This server is not running the timers (<code>RUN_SCHEDULER=0</code>). That is normal
            when a separate worker runs them — if a row below is marked Overdue, nothing is.
          </div>
        )}

        {notice && (
          <div role="status" style={{ padding: 12, marginBottom: 16, borderRadius: 8,
                                      fontSize: 13, background: "#ecfdf5", color: "#065f46" }}>
            {notice}
          </div>
        )}

        <div style={{ overflowX: "auto" }}>
          <table className="responsive-table" style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
                <th style={th}>Job</th>
                <th style={th}>Last run</th>
                <th style={th}>Next</th>
                <th style={th} />
              </tr>
            </thead>
            <tbody>
              {status.jobs.map(j => {
                const look = STATUS_LOOK[j.last_status] || null;
                return (
                  <tr key={j.job} style={{ borderBottom: "1px solid #f3f4f6" }}>
                    <td style={td} data-label="Job">
                      <div style={{ fontWeight: 600 }}>{JOB_LABELS[j.job] || j.job}</div>
                      {JOB_DESCRIPTIONS[j.job] && (
                        <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2, maxWidth: 420 }}>
                          {JOB_DESCRIPTIONS[j.job]}
                        </div>
                      )}
                      {!j.registered && (
                        <div style={{ fontSize: 12, color: "#d97706" }}>
                          Scheduled, but this build has nothing to run for it.
                        </div>
                      )}
                    </td>
                    <td style={td} data-label="Last run">
                      {j.last_run ? (
                        <>
                          <div>{formatWhen(j.last_run, { zone })}</div>
                          {look && <Badge color={look.color}>{look.label}</Badge>}
                          {j.last_detail && (
                            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                              {j.last_detail}
                            </div>
                          )}
                        </>
                      ) : <span style={{ color: "#6b7280" }}>Never</span>}
                      {/* The one signal that survived the Missed column: the
                          job has not run since an occurrence it should have
                          served. Since 2026-10-30 the API sends it only when
                          the job has STOPPED — the first occurrence missed after
                          the newest one that ran. */}
                      {j.overdue_since && (
                        <div style={{ marginTop: 4 }}>
                          <Badge color="#dc2626">Overdue</Badge>{" "}
                          <span style={{ fontSize: 12, color: "#dc2626" }}>
                            nothing ran for {formatWhen(j.overdue_since, { zone })} or since
                          </span>
                        </div>
                      )}
                      {/* A gap that later runs closed (a worker pod replaced):
                          information, muted, never Overdue. It had kept every
                          job Overdue for 14 days on dev. */}
                      {j.last_gap && (
                        <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                          missed {formatWhen(j.last_gap, { zone })}, ran again since
                        </div>
                      )}
                    </td>
                    <td style={td} data-label="Next">{formatWhen(j.next_run, { zone })}</td>
                    <td style={td} data-label="">
                      <Btn onClick={() => runNow(j.job)} disabled={busy === j.job || !j.registered}>
                        {busy === j.job ? "Running…" : "Run now"}
                      </Btn>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p style={{ margin: "12px 0 0", fontSize: 12, color: "#6b7280" }}>
          <strong>Run now</strong> serves the occurrence that is currently due. Pressing it after a
          successful run does nothing, so it cannot send anything a second time. The PPMS
          Catalogue is the exception: it sends nothing, so its Run now refreshes every time.
        </p>
      </Card>

      <Card>
        <h3 style={{ margin: "0 0 4px", fontSize: 16 }}>Notifications queued</h3>
        <p style={{ margin: "0 0 16px", fontSize: 13, color: "#6b7280" }}>
          One row per recipient, which is the level people actually ask about — a log line can only
          say a run happened. <strong>Unknown</strong> means the attempt ended with no answer from
          the mail server: it is never retried, because a retry might deliver a second copy.
        </p>

        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 16 }}>
          {Object.entries(outbox?.counts || {}).map(([k, n]) => (
            <div key={k}>
              <div style={{ fontSize: 20, fontWeight: 700, color: OUTBOX_LOOK[k]?.color || G_DARK }}>{n}</div>
              <div style={{ fontSize: 12, color: "#6b7280" }}>{OUTBOX_LOOK[k]?.label || k}</div>
            </div>
          ))}
        </div>

        {(outbox?.messages || []).length === 0 ? (
          <p style={{ fontSize: 14, color: "#6b7280" }}>Nothing has been queued yet.</p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="responsive-table" style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
                  <th style={th}>Recipient</th>
                  <th style={th}>Period</th>
                  <th style={th}>Status</th>
                  <th style={th}>When</th>
                </tr>
              </thead>
              <tbody>
                {outbox.messages.map(m => {
                  const look = OUTBOX_LOOK[m.status] || { label: m.status, color: "#6b7280" };
                  return (
                    <tr key={m.id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                      <td style={td} data-label="Recipient">
                        <div>{m.recipient}</div>
                        <div style={{ fontSize: 12, color: "#6b7280" }}>{m.subject}</div>
                      </td>
                      <td style={td} data-label="Period">{m.period}</td>
                      <td style={td} data-label="Status">
                        <Badge color={look.color}>{look.label}</Badge>
                        {m.attempts > 1 && (
                          <div style={{ fontSize: 12, color: "#6b7280" }}>
                            {m.attempts} of {outbox.max_attempts} attempts
                          </div>
                        )}
                        {m.last_error && (
                          <div style={{ fontSize: 12, color: "#dc2626", marginTop: 4 }}>
                            {m.last_error}
                          </div>
                        )}
                      </td>
                      <td style={td} data-label="When">
                        {formatWhen(m.sent_at || m.created_at, { zone })}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
